import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Loader2, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useUpdateDepartment } from '@/hooks/useDepartments'
import { updateDepartmentSchema, type UpdateDepartmentFormData } from '@/schemas/department'
import type { ApiDepartment } from '@/types/department'
import { showError } from '@/lib/error'

interface EditDepartmentDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	department: ApiDepartment | null
	onSuccess?: () => void
}

export function EditDepartmentDialog({
	open,
	onOpenChange,
	department,
	onSuccess,
}: EditDepartmentDialogProps) {
	const updateMutation = useUpdateDepartment()

	const form = useForm<UpdateDepartmentFormData>({
		resolver: zodResolver(updateDepartmentSchema),
		defaultValues: {
			name: '',
			description: '',
			status: 1,
		},
	})

	// 当部门数据变化时更新表单
	useEffect(() => {
		if (open && department) {
			form.reset({
				name: department.name,
				description: department.description || '',
				status: department.status,
			})
		}
	}, [open, department, form])

	const onSubmit = async (data: UpdateDepartmentFormData) => {
		if (!department) return

		try {
			await updateMutation.mutateAsync({
				id: department.id,
				data: {
					name: data.name,
					description: data.description || undefined,
					status: data.status,
				},
			})
			toast.success('部门更新成功')
			onOpenChange(false)
			onSuccess?.()
		} catch (error) {
			console.error('更新部门失败:', error)
			showError(error, '更新部门失败')
		}
	}

	const handleClose = () => {
		form.reset()
		onOpenChange(false)
	}

	if (!department) return null

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[450px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Building2 className="h-5 w-5" />
						编辑部门
					</DialogTitle>
					<DialogDescription>
						修改部门「{department.name}」的信息
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>部门名称 <span className="text-destructive">*</span></FormLabel>
									<FormControl>
										<Input placeholder="请输入部门名称" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>部门说明</FormLabel>
									<FormControl>
										<textarea
											className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
											placeholder="请输入部门说明（可选）"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={handleClose}>
								取消
							</Button>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								保存
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
